import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { loadPivotView, syncToLocalStorage } from "./pivotBuilder/utils/storage";
import { CreatePivotBuilderPage } from "./CreatePivotBuilderPage";
import { SavedPivotView } from "./pivotBuilder/types";
import { getPivotView } from "../../data/api/pivotApi";

export const EditPivotViewPage: React.FC = () => {
  const { viewId } = useParams<{ viewId: string }>();
  const navigate = useNavigate();
  const [savedView, setSavedView] = useState<SavedPivotView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadView = async () => {
      if (!viewId) {
        navigate("/cfo/registers/uploadv1");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Try to load from API first
        try {
          const view = await getPivotView(viewId);
          setSavedView(view);
          // Sync to localStorage for offline support
          syncToLocalStorage([view]);
        } catch (apiError: any) {
          console.warn("Failed to load view from API, trying localStorage:", apiError);
          // Fallback to localStorage
          const localView = loadPivotView(viewId);
          if (localView) {
            setSavedView(localView);
          } else {
            throw new Error("Pivot view not found in API or local storage");
          }
        }
      } catch (err: any) {
        console.error("Failed to load pivot view:", err);
        setError(err.message || "Failed to load pivot view");
        alert("Pivot view not found. Redirecting to library...");
        navigate("/cfo/registers/uploadv1");
        return;
      } finally {
        setLoading(false);
      }
    };

    loadView();
  }, [viewId, navigate]);

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-body font-body text-subtext-color">Loading pivot view...</p>
        </div>
      </DefaultPageLayout>
    );
  }

  if (error || !savedView) {
    return null; // Navigation will handle redirect
  }

  // Pass the saved view data to CreatePivotBuilderPage for editing
  return <CreatePivotBuilderPage editViewId={viewId} editViewData={savedView} />;
};

